import { DateConstraintEnum, DateConstraintType, DateSensEnum, DateUnitEnum, FixedDateType, RelativeDateType } from '../../types/Question';
import { numberToIsoDate } from '../number';

const computeFixedDate = ({ fixed_date }: FixedDateType): string => fixed_date;

type ComputeFunctionType = (value: number, quantity: number) => number;

const functionsByDateSens: Record<DateSensEnum, ComputeFunctionType> = {
  MINUS: (value: number, quantity: number) => value - quantity,
  PLUS: (value: number, quantity: number) => value + quantity
}

type ComputeDateDetails = Record<DateUnitEnum, number>;

const getRelativeDate = (computeFct: ComputeFunctionType, quantity: number, unit: DateUnitEnum): ComputeDateDetails => {
  const now: Date = new Date();
  const dateDetails: ComputeDateDetails = {
    YEAR: now.getFullYear(),
    MONTH: now.getMonth(),
    DAY: now.getDate()
  };

  return {
    ...dateDetails,
    [unit]: computeFct(dateDetails[unit], quantity)
  };
};

// The API stores the direction as a boolean (true = plus) but the Diapason types and
// defaults use the 'PLUS'/'MINUS' strings. Only an explicit plus value may go PLUS —
// the old truthiness check sent every non-empty string (including 'MINUS') into the future.
const isPlusSens = (sens: DateSensEnum | boolean | null | undefined): boolean =>
  sens === true || `${sens}` === 'true' || `${sens}` === DateSensEnum.PLUS;

const computeRelativeDate = (constraint: RelativeDateType & { isMinDate?: boolean }): string => {
  const { todays_date_sens, todays_date_quantity: quantity, todays_date_unit: unit, isMinDate } = constraint;
  const computeFunction: ComputeFunctionType = isPlusSens(todays_date_sens) ? functionsByDateSens.PLUS : functionsByDateSens.MINUS;

  const { YEAR, MONTH, DAY }: ComputeDateDetails = getRelativeDate(computeFunction, quantity, unit);

  // A zero offset is "the day of the answer": exact today, never snapped, whatever unit
  // it was stored with.
  if (!quantity) return numberToIsoDate(YEAR, MONTH, DAY);

  // Year- and month-unit offsets snap OUTWARD to the whole period. A mid-year bound
  // (e.g. min = today − 10 years) makes some months/days of the edge year ineligible,
  // so the wheel keeps clamping and jumping while participants scroll — reported as
  // very confusing. A researcher thinking in years/months means the whole year/month;
  // day-unit offsets (and "the day of the answer") stay exact on purpose.
  if (unit === DateUnitEnum.YEAR) {
    return isMinDate ? numberToIsoDate(YEAR, 0, 1) : numberToIsoDate(YEAR, 11, 31);
  }
  if (unit === DateUnitEnum.MONTH) {
    // Day 0 of the next month = last day of MONTH (Date.UTC normalizes overflow).
    return isMinDate ? numberToIsoDate(YEAR, MONTH, 1) : numberToIsoDate(YEAR, MONTH + 1, 0);
  }
  return numberToIsoDate(YEAR, MONTH, DAY);
};

const extractFunctionByDateContraint: Record<DateConstraintEnum, (constraint: DateConstraintType) => string | undefined> = {
  FIXED_DATE: (constraint: DateConstraintType): string => computeFixedDate(constraint as FixedDateType),
  RELATIVE_DATE: (constraint: DateConstraintType): string => computeRelativeDate(constraint as RelativeDateType),
  DISABLED: (): undefined => undefined,
  NO_LIMIT: (constraint: DateConstraintType): string => {
    // "No limit" still needs concrete bounds for the wheel; ±120 years covers any
    // human birthdate (the previous ±50 cut off birth years older than 50 years).
    const todayDate = new Date()
    const dateIncreaseFactor = 120 * (constraint.isMinDate ? -1 : 1)
    const limitDate = new Date((todayDate).setFullYear((todayDate).getFullYear() + dateIncreaseFactor))
    return limitDate.toISOString()
  }
};

export const computeDateLimit = (constraint: DateConstraintType): string | undefined => {
  const { date_type } = constraint;
  return extractFunctionByDateContraint[date_type](constraint);
};
